'use client';

import { useCallback, useState } from 'react';
import { Checkbox, CheckboxSize, CheckboxType } from '@nx-frontend/shared/ui/checkbox';
import { ContextMenuItemData, ContextMenuItemType } from '@nx-frontend/shared/types';

interface ContextMenuItemSwitchParams {
  icon?: React.ReactElement;
  onChange: (checked: boolean) => void;
  text: string;
  id: string;
  isSeparator?: boolean;
  // checkbox initial state. Any changes to this field will be ignored by the component.
  checked?: boolean;
}

function MenuItemSwitch(props: ContextMenuItemSwitchParams) {
  // We use state-controlled checkbox here, but it might not be appropriate for all use cases
  // because it changes checked state as soon as user clicked on it and does not wait for props change.
  // So it treat checked in props as initial state only.
  const [checked, setChecked] = useState(Boolean(props.checked));

  const onChange = useCallback(
    (newChecked: boolean) => {
      setChecked(newChecked);
      props.onChange(newChecked);
    },
    [props]
  );

  return props.icon ? (
    <div
      className="flex flex-row w-full justify-between items-center pb-1"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex gap-2 items-center w-full">
        {props.icon}
        <span className="text-sm">{props.text}</span>
        <span className="self-end ml-auto">
          <Checkbox
            id={props.id + '-cb'}
            checked={checked}
            type={CheckboxType.SWITCH}
            boxSize={CheckboxSize.SM}
            onChange={onChange}
          />
        </span>
      </div>
    </div>
  ) : (
    <div
      className="flex flex-row w-full justify-between items-center pb-1"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex gap-2 items-center">
        <Checkbox
          id={props.id + '-cb'}
          checked={checked}
          type={CheckboxType.SWITCH}
          boxSize={CheckboxSize.SM}
          onChange={onChange}
        />
        <span className="text-sm">{props.text}</span>
      </div>
    </div>
  );
}

export function getContextMenuItemSwitch(params: ContextMenuItemSwitchParams): ContextMenuItemData {
  // in these components we are stopping propagation because context menu should not close when clicking on switch
  return {
    id: params.id,
    type: ContextMenuItemType.HTML,
    html: <MenuItemSwitch {...params} />,
    isSeparator: Boolean(params.isSeparator),
  };
}
